import { Link } from 'react-router-dom';
import { Building2, CalendarClock, MessageSquare } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { FxRates, Lead } from '../../lib/leadsApi';
import { Avatar } from '../tasks/linear/Avatar';
import { LabelPill } from '../tasks/LabelPill';
import {
  TEMPERATURE_DOT,
  TEMPERATURE_LABEL,
  formatLeadValue,
  formatShortDate,
  isOverdue,
} from './leadConstants';

interface LeadCardProps {
  lead: Lead;
  // The soonest incomplete next-action due date, if any (computed by the board).
  nextActionDue?: string | null;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  // Currency picked on the page; values in another one are converted at BNR rate.
  displayCurrency?: string;
  rates?: FxRates | null;
}

/**
 * A single lead card on the Kanban board (CLE-191, spec 3.2): temperature dot,
 * title, company, value, assignee avatar, next-action due (red if overdue),
 * label pills and comment count. In an onboarding pipeline the card is a
 * client being onboarded, so it links to that client.
 */
export function LeadCard({
  lead,
  nextActionDue,
  onClick,
  onDragStart,
  displayCurrency,
  rates,
}: LeadCardProps) {
  const { t } = useI18n();
  const overdue = isOverdue(nextActionDue);
  const money =
    lead.value != null ? formatLeadValue(lead.value, lead.currency, displayCurrency, rates) : null;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="cursor-pointer rounded-md border border-gray-200 bg-white p-3 shadow-sm transition hover:border-brand-300 hover:shadow"
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-[11px] text-gray-400">
          <span
            aria-hidden
            title={TEMPERATURE_LABEL[lead.temperature]}
            className={`h-2 w-2 shrink-0 rounded-full ${TEMPERATURE_DOT[lead.temperature]}`}
          />
          {lead.identifier}
        </span>
        {lead.value != null && (
          <span className="text-[11px] font-semibold text-gray-700" title={money?.title}>
            {money?.text}
          </span>
        )}
      </div>

      <p className="mt-1 text-sm font-medium text-gray-900">{lead.title}</p>
      {lead.companyName && (
        <p className="mt-0.5 truncate text-[11px] text-gray-500">{lead.companyName}</p>
      )}
      {lead.pipeline?.kind === 'ONBOARDING' && lead.clientId && (
        <Link
          to={`/clients/${lead.clientId}`}
          onClick={(e) => e.stopPropagation()}
          draggable={false}
          className="mt-1.5 inline-flex items-center gap-1 rounded bg-brand-500/10 px-1.5 py-0.5 text-[11px] font-medium text-brand-600 hover:underline"
        >
          <Building2 size={11} /> {t('leads.clientBadge')}
        </Link>
      )}

      {lead.labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {lead.labels.map((l) => (
            <LabelPill key={l.id} label={l} />
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
        {nextActionDue && (
          <span
            className={`inline-flex items-center gap-0.5 ${
              overdue ? 'font-medium text-red-600' : ''
            }`}
            title={overdue ? 'Următoarea acțiune este întârziată' : 'Termenul următoarei acțiuni'}
          >
            <CalendarClock size={11} /> {formatShortDate(nextActionDue)}
          </span>
        )}
        {lead.commentCount > 0 && (
          <span className="inline-flex items-center gap-0.5">
            <MessageSquare size={11} /> {lead.commentCount}
          </span>
        )}
        {lead.assignee && (
          <span className="ml-auto inline-flex">
            <Avatar name={lead.assignee.name} userId={lead.assignee.id} size={20} />
          </span>
        )}
      </div>
    </div>
  );
}
